/**
 * Post-Crater Smoothing — column-height averaging to smooth jagged crater rims.
 *
 * After an explosion removes sand cells, the crater rim has sharp edges.
 * Smoothing regions animate these rims toward their neighbor averages over
 * ~0.4s. Pure arithmetic (no RNG) = naturally deterministic for multiplayer.
 *
 * Replaces the old cellular automata sand physics system.
 */

import { CELL, TERRAIN, SMOOTHING, SAND_GEN } from '../constants';
import { getGrid, rebuildColumnTopCacheRange, removeThinSand } from './grid';
import type { GameState, SmoothingRegion } from '../types';
import type { GameEffect } from '../game/effects';

// GRID_WIDTH accessed via TERRAIN.GRID_WIDTH (dynamic for wide stages)

/** Initialize smoothing system (no-op — no RNG needed). */
export function initSmoothing(): void {
  // Intentionally empty — smoothing uses pure averaging, no seeded RNG.
}

/** Clear all active regions (called on game reset). */
export function resetSmoothing(state: GameState): void {
  state.smoothingRegions = [];
}

/** Spawn a smoothing region at crater bounds. Merges overlapping existing regions. */
export function spawnSmoothingRegion(
  state: GameState,
  bounds: { minGx: number; maxGx: number; minGy?: number; maxGy?: number },
): void {
  const expand = SMOOTHING.REGION_EXPAND;
  let minGx = Math.max(0, bounds.minGx - expand);
  let maxGx = Math.min(TERRAIN.GRID_WIDTH - 1, bounds.maxGx + expand);

  // Merge with overlapping existing regions (1D overlap on column range)
  const kept: SmoothingRegion[] = [];
  for (const r of state.smoothingRegions) {
    if (r.minGx <= maxGx && r.maxGx >= minGx) {
      // Overlapping — absorb into new region
      minGx = Math.min(minGx, r.minGx);
      maxGx = Math.max(maxGx, r.maxGx);
    } else {
      kept.push(r);
    }
  }

  kept.push({
    minGx, maxGx,
    framesRemaining: SMOOTHING.FRAMES,
  });

  // Cap regions — drop oldest if exceeded
  while (kept.length > SMOOTHING.MAX_REGIONS) {
    kept.shift();
  }

  state.smoothingRegions = kept;
}

/** True when no active smoothing regions remain (turn gate). */
export function allSmoothingComplete(state: GameState): boolean {
  return state.smoothingRegions.length === 0;
}

/**
 * Find the topmost sand cell in a column (grid Y).
 * Returns -1 if column has no sand.
 */
function findColumnSandTop(grid: Uint8Array, gx: number): number {
  for (let gy = 0; gy < TERRAIN.GRID_HEIGHT; gy++) {
    if (grid[gy * TERRAIN.GRID_WIDTH + gx] === CELL.SAND) return gy;
  }
  return -1;
}

/**
 * Find the bottommost sand cell in a contiguous sand column starting from sandTop.
 * Stops at wireframe or empty gap.
 */
function findColumnSandBottom(grid: Uint8Array, gx: number, sandTop: number): number {
  let gy = sandTop;
  while (gy + 1 < TERRAIN.GRID_HEIGHT && grid[(gy + 1) * TERRAIN.GRID_WIDTH + gx] === CELL.SAND) {
    gy++;
  }
  return gy;
}

/**
 * Run one smoothing frame across all active regions.
 * Returns terrain_update effects with skipHotEdge: true.
 */
export function updateSmoothing(state: GameState): GameEffect[] {
  const grid = getGrid();
  if (!grid || state.smoothingRegions.length === 0) return [];

  // Global dirty bounds across all regions
  let dirtyMinGx: number = TERRAIN.GRID_WIDTH;
  let dirtyMaxGx = 0;
  let dirtyMinGy: number = TERRAIN.GRID_HEIGHT;
  let dirtyMaxGy = 0;
  let anyChanged = false;

  for (const region of state.smoothingRegions) {
    // Build column sand-top array for this region
    const colCount = region.maxGx - region.minGx + 1;
    const sandTops = new Int16Array(colCount);
    for (let i = 0; i < colCount; i++) {
      sandTops[i] = findColumnSandTop(grid, region.minGx + i);
    }

    // Compute target heights via neighbor averaging, then apply
    for (let i = 0; i < colCount; i++) {
      const gx = region.minGx + i;
      const currentTop = sandTops[i];
      if (currentTop < 0) continue; // no sand in this column

      // Check if wireframe is exposed at top of column (don't add sand over it)
      if (currentTop > 0 && grid[(currentTop - 1) * TERRAIN.GRID_WIDTH + gx] === CELL.WIREFRAME) continue;

      // Get neighbor heights (use current height for void/edge neighbors — no pull toward void)
      const leftTop = i > 0 ? sandTops[i - 1] : -1;
      const rightTop = i < colCount - 1 ? sandTops[i + 1] : -1;

      // Also check beyond region boundary for edge columns
      const leftVal = leftTop >= 0
        ? leftTop
        : (i === 0 && gx > 0 ? findColumnSandTop(grid, gx - 1) : -1);
      const rightVal = rightTop >= 0
        ? rightTop
        : (i === colCount - 1 && gx < TERRAIN.GRID_WIDTH - 1 ? findColumnSandTop(grid, gx + 1) : -1);

      // Compute neighbor average
      let sum = 0;
      let count = 0;
      if (leftVal >= 0) { sum += leftVal; count++; }
      if (rightVal >= 0) { sum += rightVal; count++; }

      if (count === 0) continue; // isolated column, skip

      const neighborAvg = sum / count;
      const blended = currentTop + (neighborAvg - currentTop) * SMOOTHING.BLEND_FACTOR;
      const newTop = Math.round(blended);

      if (newTop === currentTop) continue;

      const sandBottom = findColumnSandBottom(grid, gx, currentTop);

      if (newTop > currentTop) {
        // Remove sand cells from top (lower terrain = higher gy values)
        // newTop > currentTop means terrain is being lowered (sand removed from top)
        const removeCount = Math.min(newTop - currentTop, sandBottom - currentTop + 1);
        for (let gy = currentTop; gy < currentTop + removeCount; gy++) {
          if (grid[gy * TERRAIN.GRID_WIDTH + gx] === CELL.SAND) {
            grid[gy * TERRAIN.GRID_WIDTH + gx] = CELL.EMPTY;
          }
        }
        if (removeCount > 0) {
          anyChanged = true;
          if (gx < dirtyMinGx) dirtyMinGx = gx;
          if (gx > dirtyMaxGx) dirtyMaxGx = gx;
          if (currentTop < dirtyMinGy) dirtyMinGy = currentTop;
          if (currentTop + removeCount > dirtyMaxGy) dirtyMaxGy = currentTop + removeCount;
        }
      } else {
        // Add sand cells above current top (raise terrain = lower gy values)
        const addCount = currentTop - newTop;
        for (let gy = newTop; gy < currentTop; gy++) {
          if (gy >= 0 && grid[gy * TERRAIN.GRID_WIDTH + gx] === CELL.EMPTY) {
            grid[gy * TERRAIN.GRID_WIDTH + gx] = CELL.SAND;
          }
        }
        if (addCount > 0) {
          anyChanged = true;
          if (gx < dirtyMinGx) dirtyMinGx = gx;
          if (gx > dirtyMaxGx) dirtyMaxGx = gx;
          if (newTop < dirtyMinGy) dirtyMinGy = newTop;
          if (currentTop > dirtyMaxGy) dirtyMaxGy = currentTop;
        }
      }
    }

    region.framesRemaining--;
  }

  // Remove expired regions
  state.smoothingRegions = state.smoothingRegions.filter(r => r.framesRemaining > 0);

  // Remove thin sand debris left by smoothing
  for (const region of state.smoothingRegions) {
    if (removeThinSand(region.minGx, region.maxGx, 0, TERRAIN.GRID_HEIGHT - 1, SAND_GEN.MIN_THICKNESS)) {
      anyChanged = true;
      if (region.minGx < dirtyMinGx) dirtyMinGx = region.minGx;
      if (region.maxGx > dirtyMaxGx) dirtyMaxGx = region.maxGx;
    }
  }

  // Rebuild caches and emit effect if any sand changed
  if (!anyChanged) return [];

  rebuildColumnTopCacheRange(dirtyMinGx, dirtyMaxGx);

  return [{
    type: 'terrain_update',
    bounds: { minGx: dirtyMinGx, maxGx: dirtyMaxGx, minGy: dirtyMinGy, maxGy: dirtyMaxGy },
    skipHotEdge: true,
  }];
}
